import { useTranslation } from "react-i18next";
import { usePublicPlans } from "@/api/queries/publicPlans";
import { pickI18nBlob } from "@/components/admin/I18nField";

/** Plan names come from the same grants as the pricing comparison.
 *
 *  ``inline`` renders the sentence as a ``<span>`` so a caller can append it
 *  to its own paragraph. The FAQ and /how-it-works used to print a vague
 *  "depends on your plan" sentence AND this one underneath it — the same
 *  fact twice (KAN-1946). Now the vague one is gone and this one carries
 *  the plan names. */
export function PlatformBrandingNotice({
  inline = false,
}: { inline?: boolean } = {}): JSX.Element | null {
  const { t, i18n } = useTranslation("common");
  const plans = usePublicPlans();
  const ordered = [...(plans.data?.items ?? [])].sort(
    (a, b) => a.sort_order - b.sort_order,
  );
  const nameFor = (...grants: string[]) => {
    const plan = ordered.find((p) =>
      grants.every((g) => p.default_entitlements.includes(g)),
    );
    return plan
      ? pickI18nBlob(plan.name_i18n, plan.name, plan.name_en, i18n.language)
      : null;
  };
  const domainPlan = nameFor("custom_domain");
  const brandingPlan = nameFor("white_label_invoices", "white_label_emails");
  if (!domainPlan || !brandingPlan) return null;
  const text = t("platformBrandingNotice", { domainPlan, brandingPlan });
  if (inline) return <span data-testid="platform-branding-notice">{text}</span>;
  return (
    <p className="mt-3 text-sm text-ink-2">
      {text}
    </p>
  );
}
